<template>
  <div>
    <div class="login-header">
      <h2>{{ t('lockedTitle') }}</h2>
    </div>

    <div class="locked-card">
      <p>{{ t('lockedMessage') }}</p>
      <p class="locked-until">
        {{ t('lockedUntil', { until: formattedUntil }) }}
      </p>
      <p v-if="remainingLabel" class="locked-remaining">{{ remainingLabel }}</p>
    </div>

    <button class="secondary-btn" @click="$emit('retry')">
      {{ t('lockedRetry') }}
    </button>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'

export default defineComponent({
  name: 'LockedView',

  props: {
    lockedUntil: { type: Number, default: 0 }
  },

  emits: ['retry'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return { now: Date.now(), timer: null }
  },

  computed: {
    formattedUntil() {
      if (!this.lockedUntil) return ''
      try { return new Date(this.lockedUntil).toLocaleString() }
      catch (e) { return String(this.lockedUntil) }
    },
    /**
     * Live "X min Y s" countdown. Only formatted up to hours — the
     * server-side default lock is in the minutes range, so anything
     * longer than that just shows raw hours.
     */
    remainingLabel() {
      const ms = this.lockedUntil - this.now
      if (ms <= 0) return ''
      const totalSec = Math.floor(ms / 1000)
      const hours = Math.floor(totalSec / 3600)
      const mins = Math.floor((totalSec % 3600) / 60)
      const secs = totalSec % 60
      if (hours > 0) return `${hours}h ${mins}m`
      if (mins > 0) return `${mins}m ${secs}s`
      return `${secs}s`
    }
  },

  watch: {
    now() {
      // Auto-clear the lock view when the lockedUntil ms has passed —
      // emit retry so the orchestrator drops us back to LoginView.
      if (this.lockedUntil && this.now >= this.lockedUntil) {
        this.stopTimer()
        this.$emit('retry')
      }
    }
  },

  mounted() {
    this.timer = setInterval(() => { this.now = Date.now() }, 1000)
  },

  beforeUnmount() {
    this.stopTimer()
  },

  methods: {
    stopTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    }
  }
})
</script>

<style scoped>
.login-header { margin-bottom: 12px; }
.login-header h2 { margin: 0; font-size: 1.25rem; color: #b91c1c; }
.locked-card {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #7f1d1d;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 12px;
}
.locked-card p { margin: 0 0 6px 0; }
.locked-card p:last-child { margin-bottom: 0; }
.locked-until { font-weight: 600; }
.locked-remaining { font-variant-numeric: tabular-nums; }
.secondary-btn {
  padding: 10px 14px;
  background: #f0f2f5;
  color: #1a1a2e;
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  width: 100%;
}
.secondary-btn:hover { background: #e4e8ee; }
</style>
